// Product types of a procurement. An LPG tender can buy several product types,
// each with its own composition; every other product has exactly one. The list
// lives in form.procurementProductTypes; its first entry is the primary product
// type (form.productTypeId) that other modules still read.

export const LPG_PRODUCT = ['4588b5e6-7309-49d0-9107-59cbb96b6bd8'];

// Mixed propane & butane types: composition per cargo type (Full/Half VLGC,
// Semi Refrigerated) and a Cargo Type per delivery.
export const TARGET_PRODUCT_TYPES_MIX = [
  '4588b5e6-7309-49d0-9107-59cbb96b6b18', // Refrigerated Propane & Butane
  '4588b5e6-7309-49d0-9107-59cbb96b6b19', // Pressurized Propane & Butane
];

export const isLpgProduct = productNameId =>
  LPG_PRODUCT.includes(productNameId);

export const isMixProductType = productTypeId =>
  TARGET_PRODUCT_TYPES_MIX.includes(productTypeId);

// Composition headings shown for each LPG product type.
const COMPOSITION_TITLES = {
  '4588b5e6-7309-49d0-9107-59cbb96b6b11': ['Refrigerated Propane'],
  '4588b5e6-7309-49d0-9107-59cbb96b6b12': ['Refrigerated Butane'],
  '4588b5e6-7309-49d0-9107-59cbb96b6b13': ['LPG Mixed Pressurized'],
  '4588b5e6-7309-49d0-9107-59cbb96b6b14': ['Pressurized Propane'],
  '4588b5e6-7309-49d0-9107-59cbb96b6b15': ['Pressurized Butane'],
  '4588b5e6-7309-49d0-9107-59cbb96b6b18': [
    'Refrigerated Propane',
    'Refrigerated Butane',
  ],
  '4588b5e6-7309-49d0-9107-59cbb96b6b19': [
    'Pressurized Propane',
    'Pressurized Butane',
  ],
};

export const compositionTitles = productTypeId => {
  const [comptitle = '', sectitle = ''] =
    COMPOSITION_TITLES[productTypeId] || [];
  return { comptitle, sectitle };
};

// Composition fields of one product type entry. The VLGC / semi names match
// the procurement's own fields so the composition components can edit either.
const MIX_FIELDS = [
  'isFullCargoVLGC',
  'fullCargoVLGCFrequency',
  'fullCargoVLGCRefrigratePropaneMin',
  'fullCargoVLGCRefrigratePropaneMax',
  'fullCargoVLGCRefrigrateButaneMin',
  'fullCargoVLGCRefrigrateButaneMax',
  'fullCargoVLGCQuantityStep',
  'isHalfCargoVLGC',
  'halfCargoVLGCFrequency',
  'halfCargoVLGCRefrigratePropaneMin',
  'halfCargoVLGCRefrigratePropaneMax',
  'halfCargoVLGCRefrigrateButaneMin',
  'halfCargoVLGCRefrigrateButaneMax',
  'halfCargoVLGCQuantityStep',
  'isSemiRefrigerated',
  'semiRefrigeratedFrequency',
  'semiRefrigeratedRefrigratePropaneMin',
  'semiRefrigeratedRefrigratePropaneMax',
  'semiRefrigeratedRefrigrateButaneMin',
  'semiRefrigeratedRefrigrateButaneMax',
  'semiRefrigeratedQuantityStep',
];
const COMPOSITION_FIELDS = [
  'compositionMin',
  'compositionMax',
  'frequency',
  'compositionQuantityStep',
];

const pickComposition = source =>
  Object.fromEntries(
    [...COMPOSITION_FIELDS, ...MIX_FIELDS].map(key => [
      key,
      source?.[key] ?? (key.startsWith('is') ? false : null),
    ])
  );

export const emptyProductTypeEntry = (productTypeId, meta = {}) => ({
  id: null,
  productTypeId,
  productTypeName: meta.label ?? null,
  uom: meta.uom ?? null,
  ...pickComposition({}),
});

// From GET /procurement/{id}. Procurements saved before product type rows
// existed fall back to the single product type and its composition.
export const productTypeEntriesFromDetail = data => {
  if (
    Array.isArray(data.procurementProductTypes) &&
    data.procurementProductTypes.length > 0
  ) {
    return data.procurementProductTypes.map(row => ({
      id: row.id ?? null,
      productTypeId: row.productTypeId,
      productTypeName: row.productTypeName ?? null,
      uom: row.uoM ?? row.uom ?? null,
      ...pickComposition(row),
    }));
  }
  if (!data.productTypeId) return [];
  return [
    {
      id: null,
      productTypeId: data.productTypeId,
      productTypeName: data.productType?.name ?? null,
      uom: data.productType?.uoM ?? null,
      ...pickComposition({ ...data, frequency: data.refButaneFrequency }),
    },
  ];
};

export const productTypeEntriesToPayload = entries =>
  (entries || [])
    .filter(entry => entry.productTypeId)
    .map(({ id, productTypeId, ...rest }) => ({
      id: id || null,
      productTypeId,
      ...pickComposition(rest),
    }));

// The primary entry's composition, written onto the procurement's own fields.
export const primaryCompositionFields = entry => {
  const composition = pickComposition(entry || {});
  const { frequency, ...rest } = composition;
  return { ...rest, refButaneFrequency: frequency };
};

// "A", "A & B", "A, B & C" – what the procurement buys.
export const productTypeNames = form => {
  const names = (form.procurementProductTypes || [])
    .map(entry => entry.productTypeName)
    .filter(Boolean);
  if (names.length === 0) return form.productType?.name || null;
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
};

export const productTypeEntryFor = (form, productTypeId) =>
  (form.procurementProductTypes || []).find(
    entry => entry.productTypeId === productTypeId
  ) || null;

// Prefix for the field ids / error keys of one entry's composition, so the
// fields of several product types on the page stay unique. The primary entry
// keeps the plain ids.
export const compositionIdPrefix = index => (index === 0 ? '' : `pt${index}_`);

const COMPOSITION_RANGES = [
  { min: 'compositionMin', max: 'compositionMax', label: 'Composition' },
  {
    min: 'fullCargoVLGCRefrigratePropaneMin',
    max: 'fullCargoVLGCRefrigratePropaneMax',
    label: 'Full VLGC Propane',
  },
  {
    min: 'fullCargoVLGCRefrigrateButaneMin',
    max: 'fullCargoVLGCRefrigrateButaneMax',
    label: 'Full VLGC Butane',
  },
  {
    min: 'halfCargoVLGCRefrigratePropaneMin',
    max: 'halfCargoVLGCRefrigratePropaneMax',
    label: 'Half VLGC Propane',
  },
  {
    min: 'halfCargoVLGCRefrigrateButaneMin',
    max: 'halfCargoVLGCRefrigrateButaneMax',
    label: 'Half VLGC Butane',
  },
  {
    min: 'semiRefrigeratedRefrigratePropaneMin',
    max: 'semiRefrigeratedRefrigratePropaneMax',
    label: 'Semi Ref Propane',
  },
  {
    min: 'semiRefrigeratedRefrigrateButaneMin',
    max: 'semiRefrigeratedRefrigrateButaneMax',
    label: 'Semi Ref Butane',
  },
];

// Min > Max checks on every product type's composition, keyed by the field id
// of the offending Min input.
export const compositionRangeErrors = form => {
  const entries = form.procurementProductTypes?.length
    ? form.procurementProductTypes
    : [form];
  const named = entries.length > 1;
  const errors = {};
  entries.forEach((entry, index) => {
    const prefix = compositionIdPrefix(index);
    COMPOSITION_RANGES.forEach(({ min, max, label }) => {
      const minVal = entry[min];
      const maxVal = entry[max];
      if (
        minVal != null &&
        minVal !== '' &&
        maxVal != null &&
        maxVal !== '' &&
        Number(minVal) > Number(maxVal)
      ) {
        errors[`${prefix}${min}`] = `${
          named && entry.productTypeName ? `${entry.productTypeName} – ` : ''
        }${label}: Min cannot be greater than Max`;
      }
    });
  });
  return errors;
};
